/**
 * components/dashboard/LocationDetail.tsx
 * ─────────────────────────────────────────
 * Right-pane contextual validation detail panel.
 *
 * Shows for a selected location (matching the customer sample report):
 *  ① Drone photo (canonical filename from Fly Volume, lazy-loaded)
 *  ② Inventory Report fields — Location, Detected QR Code, Description,
 *     Description WMS, SKU Batch Number WMS, Expected Count, Drone Count,
 *     Result, Timestamp, Comments
 *  ③ Result chip (Match / Mismatch / N/A) + optional Review chip
 *  ④ AI reasoning note
 *
 * Managers/admins/super-admins get an "Edit" button next to the Result chip.
 * It toggles an edit mode in which Result, Comments, SOH Count, Drone Count,
 * and (NBC layout) LPN Detected, LPN SOH, ITEM_ID, ITEM_DESCRIPTION become
 * inputs seeded from the current values; Save PATCHes the override and
 * Cancel discards. Customers/pilots never see the button (read-only).
 */

import React, { useCallback, useEffect, useState } from 'react';

import { useAuthImage } from '@/hooks/useAuthImage';
import { updateRecordResult } from '@/api/inventory';
import { ApiError, apiClient } from '@/api/client';
import { ImageLightbox } from '@/components/ui/ImageLightbox';
import type { CustomerResult, LocationNode, ReconciliationRecord } from '@/types';
import {
  usesOatsideLayout,
  usesNbcLayout,
  usesCevaPlaceholderLayout,
  nbcIdentityHeaders,
  identityHeaders,
  countHeader,
  wmsPlateDisplay,
  type ReportProfile,
} from '@/lib/reportProfile';

/** Result options for the editor, in display order. */
const RESULT_OPTIONS: CustomerResult[] = ['Match', 'Mismatch', 'N/A'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(v: string | number | null | undefined): React.ReactNode {
  if (v == null || v === '') return <span style={{ color: 'var(--clr-txt-muted, #94a3b8)' }}>—</span>;
  return String(v);
}

/** Render the scan timestamp in Singapore time (UTC+8) to match the report. */
function fmtTimestampSGT(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Asia/Singapore',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }).replace(',', '') + ' SGT';
}

/** Parse a numeric input back to number | null (blank → null). */
function parseCount(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

// ── Result chip (Match / Mismatch / N/A) ────────────────────────────────────────

const RESULT_STYLE: Record<string, { bg: string; fg: string; icon: string }> = {
  Match:    { bg: '#dcfce7', fg: '#166534', icon: '✓' },
  Mismatch: { bg: '#fee2e2', fg: '#991b1b', icon: '✗' },
  'N/A':    { bg: '#fef3c7', fg: '#92400e', icon: '○' },
};

function ResultChip({ result }: { result: string }) {
  const s = RESULT_STYLE[result] ?? RESULT_STYLE['N/A'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: s.bg, color: s.fg, fontWeight: 600,
      padding: '2px 10px', borderRadius: 999, fontSize: '0.85rem',
    }}>
      {s.icon} {result}
    </span>
  );
}

// ── Report field row ────────────────────────────────────────────────────────────

function ReportRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="report-row">
      <span style={{ color: 'var(--clr-txt-muted, #64748b)', fontSize: '0.8rem' }}>{label}</span>
      <span style={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

// ── Photo panel ───────────────────────────────────────────────────────────────

function PhotoPanel({
  photoId,
  canonicalFilename,
  onEnlarge,
}: {
  photoId: string | null;
  canonicalFilename: string | null;
  onEnlarge?: () => void;
}) {
  const src = photoId ? `/api/inventory/photos/${photoId}/image` : null;
  const blobUrl = useAuthImage(src);

  // No photo_id resolved, or fetch definitively failed → show placeholder
  if (!photoId || blobUrl === false) {
    return (
      <div className="detail-photo detail-photo--placeholder">
        <div className="detail-photo-icon">📷</div>
        <div className="detail-photo-name">
          {canonicalFilename ?? 'No image available'}
        </div>
      </div>
    );
  }

  // photo_id is set but blob not yet ready → still loading
  if (!blobUrl) {
    return (
      <div className="detail-photo detail-photo--placeholder">
        <div className="detail-photo-icon">⏳</div>
        <div className="detail-photo-name">Loading image…</div>
      </div>
    );
  }

  return (
    <div className="detail-photo">
      <img
        src={blobUrl}
        alt={canonicalFilename ?? 'Drone photo'}
        className="detail-photo-img detail-photo-img--zoomable"
        onClick={() => photoId && onEnlarge?.()}
        title="Click to enlarge"
      />
      <div className="detail-photo-caption">
        {canonicalFilename && (
          <span className="detail-photo-filename" title={canonicalFilename}>
            {canonicalFilename}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface LocationDetailProps {
  location: LocationNode | null;
  /** photo_id can be resolved by the parent from ProcessedPhoto records */
  photoId?: string | null;
  /** Mission id — needed to confirm a 'Needs Review' photo's slot via /assign. */
  missionId?: string | null;
  /** When true (manager/admin/super_admin), an Edit button enables edit mode. */
  canEdit?: boolean;
  /** When true (admin/super_admin only), the observational empty-detector SHADOW
   *  verdict chip is shown. Never visible to customers/managers. */
  isAdmin?: boolean;
  /** Called after a successful override with the updated record, so the parent
   *  can refresh the list AND update the detail pane immediately. */
  onSaved?: (updated?: ReconciliationRecord) => Promise<void> | void;
  /** Per-customer report field set (column nomenclature). Default: reckitt. */
  profile?: ReportProfile;
}

export function LocationDetail({
  location,
  photoId = null,
  missionId = null,
  canEdit = false,
  isAdmin = false,
  onSaved,
  profile = 'reckitt',
}: LocationDetailProps) {
  // ── Edit-mode state (seeded from the selected record) ──────────────────────
  const [editing, setEditing]   = useState(false);
  const [result, setResult]     = useState<CustomerResult>('N/A');
  const [comments, setComments] = useState('');
  const [expected, setExpected] = useState('');
  const [drone, setDrone]       = useState('');
  const [damaged, setDamaged]   = useState(false);
  const [damagedNote, setDamagedNote] = useState('');
  const [detectedSku, setDetectedSku] = useState('');
  const [expectedSku, setExpectedSku] = useState('');
  const [itemCode, setItemCode] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [lightbox, setLightbox] = useState(false);

  const record = location?.record ?? null;
  const recordId = record?.id ?? null;

  // Seed editor fields from the current record; leave edit mode when the
  // selected location changes or the data refreshes.
  useEffect(() => {
    if (!record) return;
    setResult(record.customer_result);
    setComments(record.comments === '-' ? '' : record.comments);
    setExpected(record.expected_boxes == null ? '' : String(record.expected_boxes));
    const droneCount = record.ai_counted_cartons ?? record.calculated_boxes;
    setDrone(droneCount == null ? '' : String(droneCount));
    setDamaged(!!record.damaged_racking);
    setDamagedNote(record.damaged_racking_note ?? '');
    setDetectedSku(record.detected_sku ?? '');
    setExpectedSku(record.expected_sku ?? '');
    setItemCode(record.item_code ?? '');
    setItemDescription(record.item_description ?? record.product_description ?? '');
    setEditing(false);
    setError(null);
  }, [recordId, record?.customer_result, record?.comments, record?.expected_boxes,
      record?.ai_counted_cartons, record?.calculated_boxes,
      record?.damaged_racking, record?.damaged_racking_note,
      record?.detected_sku, record?.expected_sku, record?.item_code,
      record?.item_description, record?.product_description]);

  const save = useCallback(async () => {
    if (!recordId || !record) return;
    setSaving(true);
    setError(null);
    // Only send the identity-field overrides when the manager actually
    // changed them — sending the current derived value back unchanged would
    // freeze it as a permanent override, silently disconnecting the record
    // from future SOH/master-data updates it should still track.
    const detectedSkuTrim = detectedSku.trim();
    const expectedSkuTrim = expectedSku.trim();
    const itemCodeTrim = itemCode.trim();
    const itemDescriptionTrim = itemDescription.trim();
    try {
      const updated = await updateRecordResult(recordId, {
        result,
        comments,
        expected_count: parseCount(expected),
        drone_count: parseCount(drone),
        damaged_racking: damaged,
        damaged_racking_note: damaged ? (damagedNote.trim() || null) : null,
        ...(detectedSkuTrim !== (record.detected_sku ?? '')
          ? { detected_sku: detectedSkuTrim || null } : {}),
        ...(expectedSkuTrim !== (record.expected_sku ?? '')
          ? { expected_sku: expectedSkuTrim || null } : {}),
        ...(itemCodeTrim !== (record.item_code ?? '')
          ? { item_code: itemCodeTrim || null } : {}),
        ...(itemDescriptionTrim !== (record.item_description ?? record.product_description ?? '')
          ? { item_description: itemDescriptionTrim || null } : {}),
      });
      await onSaved?.(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [recordId, record, result, comments, expected, drone, damaged, damagedNote,
      detectedSku, expectedSku, itemCode, itemDescription, onSaved]);

  // ── Needs-Review confirm: assign the captured photo to a slot ──────────────
  // The photo decoded a slot code but consensus refused it (lone slot-QR ~62%,
  // or a conflict). An admin eyeballs the photo and confirms which slot it is;
  // /assign sets manual_location_override, then a re-reconcile resolves it.
  const confirmReviewSlot = useCallback(async (slot: string) => {
    const pid = record?.review_photo_id;
    if (!pid || !missionId) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.patch(
        `/api/missions/${missionId}/photos/${pid}/assign`,
        { location_code: slot },
      );
      await onSaved?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Confirm failed.');
    } finally {
      setSaving(false);
    }
  }, [record?.review_photo_id, missionId, onSaved]);

  if (!location || !record) {
    return (
      <div className="detail-panel detail-panel--empty">
        <div className="detail-empty-icon">🗂</div>
        <h3 className="detail-empty-title">Select a location</h3>
        <p className="detail-empty-sub">
          Click any location in the index panel to view the full AI audit report.
        </p>
      </div>
    );
  }

  // customer_result reflects a manager override (override_flag); result is the
  // raw engine label and would ignore overrides — use the projection here.
  const displayResult = record.customer_result ?? 'N/A';
  const droneCount = record.ai_counted_cartons ?? record.calculated_boxes;
  const nbcOhDetected = record.detected_sku ? 1 : 0;

  const inputStyle: React.CSSProperties = {
    height: 30, borderRadius: 6, border: '1px solid var(--clr-border, #d1d5db)',
    padding: '0 8px', fontSize: '0.85rem', width: '100%',
  };

  return (
    <div className="detail-panel">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="detail-header">
        <div className="detail-header-left">
          <h2 className="detail-location-code">{record.location_code}</h2>
          <div className="detail-address">
            Aisle {location.aisle} · Bay {location.bay} · Level {location.level}
          </div>
        </div>
        <div className="detail-header-right">
          <ResultChip result={displayResult} />
          {record.needs_review && (
            <span className="detail-action-chip detail-action-chip--review">
              Needs Review
            </span>
          )}
          {record.missing_photo && (
            <span className="detail-action-chip detail-action-chip--no-photo">
              No Photo
            </span>
          )}
          {record.requires_review && (
            <span className="detail-action-chip detail-action-chip--review">
              👁 Manager Review
            </span>
          )}
          {record.damaged_racking && (
            <span
              className="detail-action-chip detail-action-chip--damaged"
              title={record.damaged_racking_note || 'Racking shelf/beam flagged as damaged'}
            >
              🛠 Damaged Racking
            </span>
          )}
          {isAdmin && record.empty_detector_verdict && (
            <span
              className="detail-action-chip detail-action-chip--shadow"
              title={
                'Empty-detector SHADOW (observational — does not affect the report). ' +
                'Model call vs the reconciled result.'
              }
              style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' }}
            >
              🧪 Model: {record.empty_detector_verdict}
              {record.empty_detector_conf != null &&
                ` (${(record.empty_detector_conf * 100).toFixed(0)}%)`}
            </span>
          )}
          {canEdit && !editing && !usesCevaPlaceholderLayout(profile) && (
            <button
              className="btn btn--primary btn--sm"
              onClick={() => { setEditing(true); setError(null); }}
            >
              ✎ Edit
            </button>
          )}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div className="detail-body">
        {/* Left: photo (falls back to the captured-but-unattributed Needs-Review
            photo so a reviewer can actually see what to confirm) */}
        <div className="detail-body-left">
          <PhotoPanel
            photoId={photoId ?? record.review_photo_id ?? null}
            canonicalFilename={record.canonical_filename}
            onEnlarge={() => setLightbox(true)}
          />
          {record.needs_review && (
            <div className="detail-review-box">
              <div className="detail-review-note">
                📸 A photo was captured here, but the system could not confidently
                match it to this slot. {isAdmin
                  ? 'Check the photo, then confirm the correct location:'
                  : 'A manager/admin can confirm the correct location.'}
              </div>
              {isAdmin && (
                <div className="detail-review-actions">
                  {(record.review_suggestions ?? []).map((s) => (
                    <button
                      key={s}
                      className="btn btn--primary btn--sm"
                      disabled={saving || !missionId}
                      onClick={() => confirmReviewSlot(s)}
                      title={`Assign this photo to ${s} and re-audit`}
                    >
                      ✓ Confirm {s}{s === record.location_code ? ' (this slot)' : ''}
                    </button>
                  ))}
                </div>
              )}
              {isAdmin && (
                <div className="detail-review-sub">
                  After confirming, re-run Reconcile to recompute the result.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: inventory report fields (sample-report parity) */}
        <div className="detail-body-right">
          <section>
            <h3 className="section-title">Inventory Report</h3>
            <ReportRow label="Location"             value={record.location_code} />
            {usesCevaPlaceholderLayout(profile) ? (
              <>
                <ReportRow
                  label="Layout Status"
                  value="CEVA report layout placeholder - final UI format pending."
                />
                <ReportRow label="Result" value={<ResultChip result={displayResult} />} />
                <ReportRow label="Timestamp" value={fmtTimestampSGT(record.created_at)} />
              </>
            ) : usesNbcLayout(profile) ? (
              <>
                <ReportRow label="Aisle"            value={fmt(location.aisle)} />
                {editing ? (
                  <ReportRow
                    label={nbcIdentityHeaders(profile).detected}
                    value={
                      <input
                        type="text"
                        value={detectedSku}
                        onChange={(e) => setDetectedSku(e.target.value)}
                        disabled={saving}
                        aria-label={nbcIdentityHeaders(profile).detected}
                        style={inputStyle}
                      />
                    }
                  />
                ) : (
                  <ReportRow label={nbcIdentityHeaders(profile).detected} value={fmt(record.detected_sku)} />
                )}
                <ReportRow label="OH Detected"      value={fmt(nbcOhDetected)} />
                <ReportRow label="Timestamp"        value={fmtTimestampSGT(record.created_at)} />
                {editing ? (
                  <ReportRow
                    label={nbcIdentityHeaders(profile).wms}
                    value={
                      <input
                        type="text"
                        value={expectedSku}
                        onChange={(e) => setExpectedSku(e.target.value)}
                        disabled={saving}
                        aria-label={nbcIdentityHeaders(profile).wms}
                        style={inputStyle}
                      />
                    }
                  />
                ) : (
                  <ReportRow label={nbcIdentityHeaders(profile).wms} value={fmt(record.expected_sku)} />
                )}
                {editing ? (
                  <>
                    <ReportRow
                      label="OH SOH"
                      value={
                        <input
                          type="number"
                          value={expected}
                          onChange={(e) => setExpected(e.target.value)}
                          disabled={saving}
                          aria-label="OH SOH"
                          style={inputStyle}
                        />
                      }
                    />
                    <ReportRow
                      label="Result"
                      value={
                        <select
                          value={result}
                          onChange={(e) => setResult(e.target.value as CustomerResult)}
                          disabled={saving}
                          aria-label="Result"
                          style={{ ...inputStyle, fontWeight: 600 }}
                        >
                          {RESULT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      }
                    />
                  </>
                ) : (
                  <>
                    <ReportRow label="OH SOH" value={fmt(record.expected_boxes)} />
                    <ReportRow label="Result" value={<ResultChip result={displayResult} />} />
                  </>
                )}
                {editing ? (
                  <>
                    <ReportRow
                      label="ITEM_ID"
                      value={
                        <input
                          type="text"
                          value={itemCode}
                          onChange={(e) => setItemCode(e.target.value)}
                          disabled={saving}
                          aria-label="ITEM_ID"
                          style={inputStyle}
                        />
                      }
                    />
                    <ReportRow
                      label="ITEM_DESCRIPTION"
                      value={
                        <input
                          type="text"
                          value={itemDescription}
                          onChange={(e) => setItemDescription(e.target.value)}
                          disabled={saving}
                          aria-label="ITEM_DESCRIPTION"
                          style={inputStyle}
                        />
                      }
                    />
                  </>
                ) : (
                  <>
                    <ReportRow label="ITEM_ID"          value={fmt(record.item_code)} />
                    <ReportRow label="ITEM_DESCRIPTION" value={fmt(record.item_description ?? record.product_description)} />
                  </>
                )}
                {editing ? (
                  <>
                    <ReportRow
                      label="COMMENTS"
                      value={
                        <input
                          type="text"
                          value={comments}
                          onChange={(e) => setComments(e.target.value)}
                          disabled={saving}
                          aria-label="COMMENTS"
                          placeholder="Add comment..."
                          style={inputStyle}
                        />
                      }
                    />
                    <ReportRow
                      label="Damaged Racking"
                      value={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                            <input
                              type="checkbox"
                              checked={damaged}
                              onChange={(e) => setDamaged(e.target.checked)}
                              disabled={saving}
                              aria-label="Damaged racking shelf"
                            />
                            <span>Damaged racking shelf/beam</span>
                          </label>
                          {damaged && (
                            <input
                              type="text"
                              value={damagedNote}
                              onChange={(e) => setDamagedNote(e.target.value)}
                              disabled={saving}
                              aria-label="Damaged racking note"
                              placeholder="Note (e.g. bent beam, level C)..."
                              style={inputStyle}
                            />
                          )}
                        </div>
                      }
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 10 }}>
                      <button className="btn btn--primary btn--sm" onClick={save} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => { setEditing(false); setError(null); }}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      {error && <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>{error}</span>}
                    </div>
                  </>
                ) : (
                  <>
                    <ReportRow label="COMMENTS" value={fmt(record.comments === '-' ? null : record.comments)} />
                    {record.damaged_racking && (
                      <ReportRow
                        label="Damaged Racking"
                        value={
                          <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                            Yes{record.damaged_racking_note ? ` - ${record.damaged_racking_note}` : ''}
                          </span>
                        }
                      />
                    )}
                  </>
                )}
              </>
            ) : usesOatsideLayout(profile) ? (
              <>
                {/* Oatside-family field set (Oatside + Fonterra) — mirrors the
                    export profile: detected code · Description · WMS code ·
                    Description WMS · Item WMS (no SKU Batch / Item Prefix /
                    Product Status — those are Reckitt-only WMS columns). Only
                    the two identity-row labels differ per profile. */}
                <ReportRow label={identityHeaders(profile).detected} value={fmt(record.detected_sku)} />
                <ReportRow label="Description"       value={fmt(record.detected_description)} />
                <ReportRow label={identityHeaders(profile).wms}      value={wmsPlateDisplay(record.expected_sku)} />
                <ReportRow label="Description WMS"   value={fmt(record.product_description)} />
                <ReportRow label="Item WMS"          value={fmt(record.item_code)} />
              </>
            ) : (
              <>
                <ReportRow label="Detected QR Code"     value={fmt(record.detected_sku)} />
                <ReportRow label="Description"          value={fmt(record.detected_description)} />
                <ReportRow label="Description WMS"      value={fmt(record.product_description)} />
                <ReportRow label="SKU Batch Number WMS" value={fmt(record.sku_batch_wms)} />
                <ReportRow label="Item Prefix"     value={fmt(record.item_prefix)} />
                <ReportRow label="Product Status"  value={fmt(record.product_status)} />
              </>
            )}

            {!usesNbcLayout(profile) && !usesCevaPlaceholderLayout(profile) && (
              <>
              {editing ? (
                <>
                <ReportRow
                  label={countHeader(profile)}
                  value={
                    <input
                      type="number"
                      value={expected}
                      onChange={(e) => setExpected(e.target.value)}
                      disabled={saving}
                      aria-label={countHeader(profile)}
                      style={inputStyle}
                    />
                  }
                />
                <ReportRow
                  label="Drone Count"
                  value={
                    <input
                      type="number"
                      value={drone}
                      onChange={(e) => setDrone(e.target.value)}
                      disabled={saving}
                      aria-label="Drone Count"
                      style={inputStyle}
                    />
                  }
                />
                <ReportRow
                  label="Result"
                  value={
                    <select
                      value={result}
                      onChange={(e) => setResult(e.target.value as CustomerResult)}
                      disabled={saving}
                      aria-label="Result"
                      style={{ ...inputStyle, fontWeight: 600 }}
                    >
                      {RESULT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  }
                />
                <ReportRow
                  label="Comments"
                  value={
                    <input
                      type="text"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      disabled={saving}
                      aria-label="Comments"
                      placeholder="Add comment…"
                      style={inputStyle}
                    />
                  }
                />
                <ReportRow
                  label="Damaged Racking"
                  value={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={damaged}
                          onChange={(e) => setDamaged(e.target.checked)}
                          disabled={saving}
                          aria-label="Damaged racking shelf"
                        />
                        <span>🛠 Racking shelf/beam is damaged</span>
                      </label>
                      {damaged && (
                        <input
                          type="text"
                          value={damagedNote}
                          onChange={(e) => setDamagedNote(e.target.value)}
                          disabled={saving}
                          aria-label="Damaged racking note"
                          placeholder="Note (e.g. bent beam, level C)…"
                          style={inputStyle}
                        />
                      )}
                    </div>
                  }
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 10 }}>
                  <button className="btn btn--primary btn--sm" onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => { setEditing(false); setError(null); }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  {error && <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>{error}</span>}
                </div>
                </>
              ) : (
                <>
                <ReportRow label={countHeader(profile)} value={fmt(record.expected_boxes)} />
                <ReportRow label="Drone Count" value={fmt(droneCount)} />
                <ReportRow label="Result"      value={<ResultChip result={displayResult} />} />
                <ReportRow label="Comments"    value={fmt(record.comments === '-' ? null : record.comments)} />
                {record.damaged_racking && (
                  <ReportRow
                    label="Damaged Racking"
                    value={
                      <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                        🛠 Yes{record.damaged_racking_note ? ` — ${record.damaged_racking_note}` : ''}
                      </span>
                    }
                  />
                )}
                </>
              )}
              <ReportRow label="Timestamp"         value={fmtTimestampSGT(record.created_at)} />
              </>
            )}
          </section>
        </div>
      </div>

      {/* ── AI reasoning note ────────────────────────────────────────────────── */}
      {record.reasoning && (
        <div className="detail-reasoning">
          <span className="detail-reasoning-icon">🤖</span>
          <p className="detail-reasoning-text">{record.reasoning}</p>
        </div>
      )}

      {lightbox && (photoId ?? record.review_photo_id) && (
        <ImageLightbox
          photoId={(photoId ?? record.review_photo_id) as string}
          alt={`${record.location_code} pallet`}
          caption={record.canonical_filename}
          onClose={() => setLightbox(false)}
        />
      )}
    </div>
  );
}
